import { gql } from "@apollo/client";
import { DefaultFailureTypesIds, END_OF_SHIFT_FAILURE_CODE_NAME, FAILURE_CODE_EXCEPTIONS } from "constants/constants";

export const getAreas = gql`
    query getAreas($machine_center_id: Int!) {
        area(
            where: { machine_center_id: { _eq: $machine_center_id }, disabled_at: { _is_null: true } }
            order_by: [{ order: asc_nulls_last }, { created_at: asc }]
        ) {
            area_id
            machine_center_id
            order
            created_at
            translation {
                id
                translation_names {
                    name
                    language_id
                }
            }
            functional_locations(
                where: { disabled_at: { _is_null: true }, subordinate_to_id: { _is_null: true } }
                order_by: [{ order: asc_nulls_last }, { created_at: asc }]
            ) {
                functional_location_id
                area_id
                order
                created_at
                translation {
                    id
                    translation_names {
                        name
                        language_id
                    }
                }
            }
        }
    }
`;

export const setAreasOrderByMachineCenter = gql`
    mutation setAreasOrderByMachineCenter($areas: [area_insert_input!]!) {
        insert_area(objects: $areas, on_conflict: { constraint: area_pkey, update_columns: [order] }) {
            affected_rows
            returning {
                area_id
            }
        }
    }
`;

export const setFunctionalLocationsOrderByArea = gql`
    mutation setFunctionalLocationsOrderByArea($functional_locations: [functional_location_insert_input!]!) {
        insert_functional_location(
            objects: $functional_locations
            on_conflict: { constraint: functional_location_pkey, update_columns: [order] }
        ) {
            affected_rows
            returning {
                functional_location_id
            }
        }
    }
`;

export const upsertArea = gql`
    mutation upsertArea($area: [area_insert_input!]!) {
        insert_area(objects: $area, on_conflict: { constraint: area_pkey, update_columns: [name_translation_id] }) {
            affected_rows
        }
    }
`;

export const upsertFunctionalLocation = gql`
    mutation upsertArea($functional_location: [functional_location_insert_input!]!) {
        insert_functional_location(
            objects: $functional_location
            on_conflict: { constraint: functional_location_pkey, update_columns: [name_translation_id] }
        ) {
            affected_rows
        }
    }
`;

export const updateArea = gql`
    mutation updateArea($area_id: Int!, $changes: area_set_input!) {
        update_area(where: { area_id: { _eq: $area_id } }, _set: $changes) {
            affected_rows
            returning {
                area_id
            }
        }
    }
`;

export const updateFunctionalLocation = gql`
    mutation updateFunctionalLocation($functional_location_id: Int!, $changes: functional_location_set_input!) {
        update_functional_location(
            where: { functional_location_id: { _eq: $functional_location_id } }
            _set: $changes
        ) {
            affected_rows
            returning {
                functional_location_id
            }
        }
    }
`;

export const getFailureTypes = gql`
    query getFailureTypes {
        failure_type(where: { deleted_at: { _is_null: true } }) {
            failure_type_id
            name
            corporate_id
            code
            color_code
            is_location_specific
            default_failure_type_translations {
                failure_type_id
                language {
                    language_id
                    name
                }
                translated_name
            }
            failure_codes(where: { deleted_at: { _is_null: true } }) {
                failure_code_id
                failure_type_id
                corporate_id
                name
                primary_code_id
                code
                default_failure_code_translations {
                    failure_code_id
                    language {
                        language_id
                        name
                    }
                    translated_name
                }
            }
        }
    }
`;

export const insertFailureType = gql`
    mutation insertFailureType($failure_type: failure_type_insert_input!) {
        insert_failure_type_one(object: $failure_type) {
            name
            corporate_id
            is_location_specific
            color_code
        }
    }
`;

export const insertFailureCode = gql`
    mutation insertFailureCode($failure_code: failure_code_insert_input!) {
        insert_failure_code_one(object: $failure_code) {
            name
            primary_code_id
            corporate_id
            failure_type_id
        }
    }
`;

export const updateFailureType = gql`
    mutation updateFailureType($failure_type_id: Int!, $changes: failure_type_set_input!) {
        update_failure_type(where: { failure_type_id: { _eq: $failure_type_id } }, _set: $changes) {
            affected_rows
            returning {
                failure_type_id
            }
        }
    }
`;

export const updateFailureCode = gql`
    mutation updateFailureCode($failure_code_id: Int!, $changes: failure_code_set_input!) {
        update_failure_code(where: { failure_code_id: { _eq: $failure_code_id } }, _set: $changes) {
            affected_rows
            returning {
                failure_code_id
            }
        }
    }
`;

export const getFailureTypesByMachineCenterId = gql`
    query getFailureTypesByMachineCenterId($machine_center_id: Int!) {
        machine_center_failure_types(where: { machine_center_id: { _eq: $machine_center_id } }) {
            failure_type_id
        }
    }
`;

export const getLocationSpecificFailureCodesByFunctionalLocationId = gql`
    query getLocationSpecificFailureCodesByFunctionalLocationId($functional_location_id: Int!) {
        location_specific_functional_location_failure_code(
            where: { functional_location_id: { _eq: $functional_location_id } }
        ) {
            failure_code_id
        }
    }
`;

export const insertLocationSpecificFunctionalLocationFailureCodeRelation = gql`
    mutation insertLocationSpecificFunctionalLocationFailureCodeRelation(
        $location_specific_functional_location_failure_code: [location_specific_functional_location_failure_code_insert_input!]!
    ) {
        insert_location_specific_functional_location_failure_code(
            objects: $location_specific_functional_location_failure_code
        ) {
            returning {
                failure_code_id
                functional_location_id
            }
        }
    }
`;

export const deleteLocationSpecificFunctionalLocationFailureCodeRelation = gql`
    mutation deleteLocationSpecificFunctionalLocationFailureCodeRelation(
        $functional_location_id: Int!
        $failure_code_id: [Int!]!
    ) {
        delete_location_specific_functional_location_failure_code(
            where: {
                _and: [
                    { functional_location_id: { _eq: $functional_location_id } }
                    { failure_code_id: { _in: $failure_code_id } }
                ]
            }
        ) {
            affected_rows
        }
    }
`;

export const getNonLocationSpecificFailureCodesByMachineCenterId = gql`
    query getNonLocationSpecificFailureCodesByFunctionalLocationId($machine_center_id: Int!) {
        non_location_specific_machine_center_failure_code(where: { machine_center_id: { _eq: $machine_center_id } }) {
            failure_code_id
        }
        failure_code(
            where: {
                code: { _eq: ${END_OF_SHIFT_FAILURE_CODE_NAME} }
            }
        ) {
            failure_code_id
        }
    }
`;

export const insertNonLocationSpecificMachineCenterFailureCodeRelation = gql`
    mutation insertNonLocationSpecificMachineCenterFailureCodeRelation(
        $non_location_specific_machine_center_failure_code: [non_location_specific_machine_center_failure_code_insert_input!]!
    ) {
        insert_non_location_specific_machine_center_failure_code(
            objects: $non_location_specific_machine_center_failure_code
        ) {
            returning {
                machine_center_id
                failure_code_id
            }
        }
    }
`;

export const deleteNonLocationSpecificMachineCenterFailureCodeRelation = gql`
    mutation deleteNonLocationSpecificMachineCenterFailureCodeRelation(
        $machine_center_id: Int!
        $failure_code_id: [Int!]!
    ) {
        delete_non_location_specific_machine_center_failure_code(
            where: {
                _and: [
                    { machine_center_id: { _eq: $machine_center_id } }
                    { failure_code_id: { _in: $failure_code_id } }
                ]
            }
        ) {
            affected_rows
        }
    }
`;

export const getFailureCodeUnknown = gql`
    query getFailureCodeUnknown {
        failure_code (where:{code:{_eq: ${FAILURE_CODE_EXCEPTIONS.UNKNOWN}}, corporate_id: {_is_null: true}}) {
            failure_code_id
            name
        }
    }
`;

export const getDefaultFailureTypes = gql`
    query getDefaultFailureTypes {
        default_failure_types: failure_type(
            where: { failure_type_id: { _eq: ${DefaultFailureTypesIds.BREAK_END_OF_SHIFT} } }
        ) {
            failure_type_id
            name
            color_code
            is_location_specific
            corporate_id
            code
            default_failure_type_translations {
                failure_type_id
                language {
                    language_id
                    name
                }
                translated_name
            }
            failure_codes(where: { code: { _eq: ${END_OF_SHIFT_FAILURE_CODE_NAME} } }) {
                failure_code_id
                name
                primary_code_id
                default_failure_code_translations {
                    failure_code_id
                    language {
                        language_id
                        name
                    }
                    translated_name
                }
            }
        }
    }
`;

export const getExistingTranslation = gql`
    query getExistingTranslation($where: translation_name_bool_exp) {
        translation_name(where: $where) {
            translation_id
        }
    }
`;

export const updateTranslationNameMutation = gql`
    mutation updateTranslationName($translation_id: Int!, $language_id: Int!, $changes: translation_name_set_input!) {
        update_translation_name(
            where: { translation_id: { _eq: $translation_id }, language_id: { _eq: $language_id } }
            _set: $changes
        ) {
            affected_rows
            returning {
                translation_id
            }
        }
    }
`;

export const insertTranslationNameMutation = gql`
    mutation insertTranslationName($translation: [translation_name_insert_input!]!) {
        insert_translation_name(objects: $translation) {
            affected_rows
            returning {
                translation_id
            }
        }
    }
`;
